/**
 * Export/share session state (card t_31859fc7, implementation-plan.md §6 Phase 3).
 *
 * Owns everything that happens AFTER a clip is confirmed and rendered:
 *   - the account's tier + limits + usage (which decides the watermark and the prompts)
 *   - share links, their URLs and their access counters
 *   - highlight reels: selection → single-MP4 render → share link
 *   - the account-deletion / retention calls
 *
 * The review-first contract still holds: this store only ever shares or stitches clips the coach
 * confirmed. A `proposed` or `rejected` clip is not shareable, and the worker refuses it anyway —
 * the client just never offers it.
 */
import { create } from 'zustand';
import {
  browseClips,
  createClipShare,
  createReel,
  deleteAccount,
  getSeasons,
  getUserUsage,
  listReels,
  listShares,
  runRetention,
  revokeShare,
  uploadReelMedia,
  type BrowsedClip,
  type Reel,
  type SeasonSummary,
  type ShareLinkWithUrls,
  type StructuredClipRow,
  type UsageResponse,
} from '../api/client';
import { brandingKitToOverlay, type BrandingKit } from '../branding';
import { effectiveLabel, effectiveWindow } from '../review';
import { downloadBlob, reelFilename, renderReel, type ReelResult } from '../render';
import { useRenderStore } from './render';

/** A share link the user just created, kept so the UI can show/copy it immediately. */
export interface CreatedShare {
  clipId: string;
  token: string;
  pageUrl: string;
  streamUrl: string;
  title: string;
}

export interface ReelBuildInput {
  title: string;
  clips: StructuredClipRow[];
  /** Signed media URL (or Blob) of the session footage the segments are cut from. */
  sourceVideo: Blob | string;
  branding?: BrandingKit | null;
  quality?: 'preview' | 'export';
  token: string | null;
}

interface ExportState {
  usage: UsageResponse | null;
  usageError: string | null;
  shares: ShareLinkWithUrls[];
  sharesError: string | null;
  reels: Reel[];
  /** Set when a limit was hit — the upgrade prompt, not an error (card §4). */
  upgradePrompt: string | null;

  /** The clip id currently being shared, for the button's own busy state. */
  sharingClipId: string | null;
  createdShares: Record<string, CreatedShare>;

  /** Highlight-reel UI: the coach's selection, in the order they picked. */
  reelSelection: string[];
  reelTitle: string;
  reelRendering: boolean;
  reelProgress: { index: number; total: number } | null;
  reelResult: (ReelResult & { shareUrl: string | null; token: string | null }) | null;
  reelError: string | null;
  /** Share links created for reels, keyed by reel id, for the download/share affordances. */
  reelShares: Record<string, CreatedShare>;

  /** Season browse (card §5). */
  seasons: SeasonSummary[];
  browsedClips: BrowsedClip[];
  browseSeason: string | null;
  browseLoading: boolean;

  loadUsage: (token: string | null) => Promise<UsageResponse | null>;
  loadShares: (token: string | null) => Promise<void>;
  loadReels: (token: string | null) => Promise<void>;
  shareClip: (
    clip: StructuredClipRow,
    media: Blob | null,
    token: string | null,
    options?: { title?: string; expiresInDays?: number; durationSeconds?: number },
  ) => Promise<CreatedShare | null>;
  revoke: (shareLinkId: string, token: string | null) => Promise<void>;

  toggleReelClip: (clipId: string) => void;
  setReelSelection: (clipIds: string[]) => void;
  setReelTitle: (title: string) => void;
  buildReel: (input: ReelBuildInput) => Promise<ReelResult | null>;
  downloadReel: () => void;
  resetReel: () => void;

  loadSeasons: (token: string | null) => Promise<void>;
  loadBrowse: (token: string | null, filters?: { season?: string | null }) => Promise<void>;

  deleteAccount: (token: string | null) => Promise<{ ok: boolean; detail: string }>;
  runRetention: (token: string | null) => Promise<{ ok: boolean; detail: string }>;
  clearUpgradePrompt: () => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * A tier refusal is a prompt, not a failure: the worker answers 429 `quota_exceeded` with an
 * upgrade message in `details`. Surfacing the raw error here would make a plan limit look like a
 * bug, so the store keeps them apart.
 */
function upgradeMessageFrom(error: unknown): string | null {
  const candidate = error as { code?: string; details?: { upgrade?: { message?: string } }; message?: string };
  if (candidate?.code !== 'quota_exceeded') return null;
  return candidate.details?.upgrade?.message ?? candidate.message ?? null;
}

export const useExportStore = create<ExportState>((set, get) => ({
  usage: null,
  usageError: null,
  shares: [],
  sharesError: null,
  reels: [],
  upgradePrompt: null,
  sharingClipId: null,
  createdShares: {},
  reelSelection: [],
  reelTitle: '',
  reelRendering: false,
  reelProgress: null,
  reelResult: null,
  reelError: null,
  reelShares: {},
  seasons: [],
  browsedClips: [],
  browseSeason: null,
  browseLoading: false,

  loadUsage: async (token) => {
    try {
      const usage = await getUserUsage(token);
      // One place sets the watermark for BOTH render paths (clip queue and reel): the render
      // store owns it, and the tier is the only thing that may turn it off.
      useRenderStore.getState().setSubscriptionTier(usage.tier);
      set({ usage, usageError: null });
      return usage;
    } catch (error) {
      set({ usageError: errorMessage(error) });
      return null;
    }
  },

  loadShares: async (token) => {
    try {
      const { shares } = await listShares(token);
      set({ shares, sharesError: null });
    } catch (error) {
      set({ sharesError: errorMessage(error) });
    }
  },

  loadReels: async (token) => {
    try {
      const { reels } = await listReels(token);
      set({ reels });
    } catch (error) {
      set({ sharesError: errorMessage(error) });
    }
  },

  shareClip: async (clip, media, token, options = {}) => {
    set({ sharingClipId: clip.id, upgradePrompt: null });
    try {
      const result = await createClipShare(
        clip.id,
        {
          title: options.title ?? effectiveLabel(clip),
          expiresInDays: options.expiresInDays,
          media: media ?? undefined,
          durationSeconds: options.durationSeconds,
        },
        token,
      );
      const created: CreatedShare = {
        clipId: clip.id,
        token: result.shareLink.token,
        pageUrl: result.urls.pageUrl,
        streamUrl: result.urls.streamUrl,
        title: result.share.title,
      };
      set((state) => ({
        createdShares: { ...state.createdShares, [clip.id]: created },
        shares: [{ ...result.shareLink, urls: result.urls }, ...state.shares],
      }));
      void get().loadUsage(token);
      return created;
    } catch (error) {
      const prompt = upgradeMessageFrom(error);
      if (prompt) set({ upgradePrompt: prompt });
      else set({ sharesError: errorMessage(error) });
      return null;
    } finally {
      set({ sharingClipId: null });
    }
  },

  revoke: async (shareLinkId, token) => {
    await revokeShare(shareLinkId, token);
    await get().loadShares(token);
    await get().loadUsage(token);
  },

  toggleReelClip: (clipId) => {
    set((state) => ({
      reelSelection: state.reelSelection.includes(clipId)
        ? state.reelSelection.filter((id) => id !== clipId)
        : [...state.reelSelection, clipId],
    }));
  },

  setReelSelection: (clipIds) => set({ reelSelection: clipIds }),
  setReelTitle: (title) => set({ reelTitle: title }),

  buildReel: async (input) => {
    const state = get();
    const selected = input.clips.filter((clip) => state.reelSelection.includes(clip.id));
    if (selected.length < 3) {
      set({ reelError: 'Pick at least 3 confirmed clips for a highlight reel.' });
      return null;
    }

    set({
      reelRendering: true,
      reelError: null,
      reelResult: null,
      upgradePrompt: null,
      reelProgress: { index: 0, total: selected.length },
    });

    try {
      const overlay = brandingKitToOverlay(input.branding ?? null, {
        title: input.title,
      });
      const watermark = useRenderStore.getState().watermark;

      const reel = await renderReel(
        {
          sourceVideo: input.sourceVideo,
          segments: selected.map((clip) => {
            const { tIn, tOut } = effectiveWindow(clip);
            return { clipId: clip.id, title: effectiveLabel(clip), tIn, tOut };
          }),
          title: input.title,
          overlay,
          quality: input.quality ?? 'export',
          watermark,
        },
        {
          onProgress: (progress) =>
            set({ reelProgress: { index: progress.segmentIndex + 1, total: progress.segmentCount } }),
        },
      );

      // One MP4, one share link (card §3). The stitched blob goes up with the metadata so the
      // recipient has something to stream.
      const created = await createReel(
        {
          title: input.title,
          clipIds: selected.map((clip) => clip.id),
          media: reel.blob,
          durationSeconds: reel.duration,
        },
        input.token,
      );

      set((s) => ({
        reelResult: { ...reel, shareUrl: created.urls.pageUrl, token: created.reel.token },
        reelShares: {
          ...s.reelShares,
          [created.reel.id]: {
            clipId: created.reel.id,
            token: created.reel.token,
            pageUrl: created.urls.pageUrl,
            streamUrl: created.urls.streamUrl,
            title: input.title,
          },
        },
        reelRendering: false,
        reelProgress: null,
      }));
      void get().loadReels(input.token);
      void get().loadUsage(input.token);
      return reel;
    } catch (error) {
      const prompt = upgradeMessageFrom(error);
      if (prompt) set({ upgradePrompt: prompt, reelRendering: false, reelProgress: null });
      else set({ reelError: errorMessage(error), reelRendering: false, reelProgress: null });
      return null;
    }
  },

  downloadReel: () => {
    const reel = get().reelResult;
    if (!reel) return;
    downloadBlob(reel.blob, reelFilename(reel.title));
  },

  resetReel: () =>
    set({
      reelSelection: [],
      reelTitle: '',
      reelResult: null,
      reelError: null,
      reelProgress: null,
      reelRendering: false,
    }),

  loadSeasons: async (token) => {
    try {
      const { seasons } = await getSeasons(token);
      set({ seasons });
    } catch {
      set({ seasons: [] });
    }
  },

  loadBrowse: async (token, filters = {}) => {
    set({ browseLoading: true });
    try {
      const season = filters.season === undefined ? get().browseSeason : filters.season;
      const result = await browseClips({ season: season ?? undefined, limit: 100 }, token);
      set({
        browsedClips: result.clips,
        browseSeason: season ?? null,
        browseLoading: false,
      });
    } catch {
      set({ browsedClips: [], browseLoading: false });
    }
  },

  deleteAccount: async (token) => {
    try {
      const result = await deleteAccount(true, token);
      return {
        ok: result.cascadeComplete,
        detail: result.cascadeComplete
          ? `Deleted ${result.mediaObjects} stored objects and all database rows (cascade complete).`
          : 'Deletion ran but leftovers remain — see the response for detail.',
      };
    } catch (error) {
      return { ok: false, detail: errorMessage(error) };
    }
  },

  runRetention: async (token) => {
    try {
      const { retention, remainingMediaObjects } = await runRetention(token);
      const expired = Number((retention as { footageExpired?: number }).footageExpired ?? 0);
      return {
        ok: true,
        detail: `Retention pass expired ${expired} footage item(s); ${remainingMediaObjects} object(s) remain.`,
      };
    } catch (error) {
      return { ok: false, detail: errorMessage(error) };
    }
  },

  clearUpgradePrompt: () => set({ upgradePrompt: null }),
}));

/** Re-exported so the account screen can show a reel upload's progress without a second import. */
export { uploadReelMedia };
